import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";

import type { CollaboratorUiT } from "shared/domain/ui/collaborator.model";
import type { AccrualRowWithIndexT } from "../domain/types/accrual.types";

import { BALANCES_STRINGS } from "../constants/strings";

import { ColaboraButton, ImportProgressModal } from "components/v2";
import { Toast } from "components/toast";

import { useAccruals } from "../hooks";
import { useImportBalances } from "../hooks/useImportBalances";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useMultiStepDialog } from "hooks/ui/useMultiStepDialog";

import { useVacation } from "../../Policy/hooks";

import { SYSTEM_NAME } from "shared/constants/externalSystems";
import { IMPORT_OPTION_ID } from "../constants/importOptions";

import {
  OptionSelectionStep,
  ImportBaseSystemStep,
  ImportTemplateStep,
} from "./Steps";

const BALANCES_IMPORT_TEXTS = {
  loadingHeader: BALANCES_STRINGS.IMPORT_PROGRESS_HEADER,
  loadingMessage: BALANCES_STRINGS.IMPORT_PROGRESS_MESSAGE,
  successHeader: BALANCES_STRINGS.IMPORT_SUCCESS_HEADER,
  successMessage: BALANCES_STRINGS.IMPORT_SUCCESS_MESSAGE,
  errorHeader: BALANCES_STRINGS.IMPORT_ERROR_HEADER,
  errorMessage: BALANCES_STRINGS.IMPORT_ERROR_MESSAGE,
};

const BALANCES_TEMPLATE_IMPORT_TEXTS = {
  loadingHeader: BALANCES_STRINGS.IMPORT_PROGRESS_HEADER,
  loadingMessage: BALANCES_STRINGS.IMPORT_PROGRESS_MESSAGE,
  successHeader: BALANCES_STRINGS.IMPORT_SUCCESS_HEADER,
  successMessage: BALANCES_STRINGS.IMPORT_SUCCESS_MESSAGE,
  errorHeader: BALANCES_STRINGS.IMPORT_ERROR_HEADER,
  errorMessage: BALANCES_STRINGS.IMPORT_TEMPLATE_ERROR_MESSAGE,
};

type InitialLoadDialogPropsT = {
  onHide: () => void;
  collaborators: CollaboratorUiT[];
  sort: unknown; // This is because the sort function still exist as JavaScript and we haven't defined its type yet, we can replace it with the correct type when it's defined.
  hasBalances: boolean;
  allCollaboratorsHaveBalances: boolean;
  onImportSuccess: () => void;
};

type ProgressModalStateT =
  | { status: "loading" }
  | { status: "success" }
  | {
      status: "error";
      message?: string;
      errors?: Array<{
        rowNumber: number;
        collaborator: string;
        error: string;
      }>;
    }
  | null;

const InitialLoadDialog = ({
  onHide,
  collaborators,
  sort,
  hasBalances,
  allCollaboratorsHaveBalances,
  onImportSuccess,
}: InitialLoadDialogPropsT) => {
  const company = useCurrentCompany();

  const [selectedOption, setSelectedOption] = useState<
    (typeof IMPORT_OPTION_ID)[keyof typeof IMPORT_OPTION_ID]
  >(IMPORT_OPTION_ID.BASE_SYSTEM);

  const [importOption, setImportOption] = useState<"all" | "no-balances">(
    hasBalances ? "no-balances" : "all",
  );
  const [progressModal, setProgressModal] = useState<ProgressModalStateT>(null);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const [parsedRows, setParsedRows] = useState<AccrualRowWithIndexT[]>([]);

  const {
    loadInitialData: { mutateAsync: loadInitialDataAsync, isPending },
  } = useAccruals({ colaboraInstanceId: company?.instanceId ?? "" });

  const baseSystemName =
    SYSTEM_NAME.byId[
      company?.externalTypeId as keyof typeof SYSTEM_NAME.byId
    ] ?? SYSTEM_NAME.NOMINAS;

  useEffect(() => {
    if (company?.externalTypeId && baseSystemName === SYSTEM_NAME.NOMINAS) {
      setSelectedOption(IMPORT_OPTION_ID.TEMPLATE);
    }
  }, [company?.externalTypeId, baseSystemName]);

  const { data: vacationType } = useVacation({
    colaboraInstanceId: company?.instanceId,
  });

  const { mutate: doImport } = useImportBalances({
    colaboraInstanceId: company?.instanceId,
    typeId: vacationType?.type?.id,
    externalTypeId: company?.externalTypeId,
  });

  const onSelectOption = (
    opt: (typeof IMPORT_OPTION_ID)[keyof typeof IMPORT_OPTION_ID],
  ) => {
    setSelectedOption(opt);
    setIsNextButtonDisabled(false);
  };

  const steps = useMemo(
    () => [
      {
        id: "option-selection",
        Component: (
          <OptionSelectionStep
            selectedOption={selectedOption}
            onSelect={onSelectOption}
            baseSystemName={baseSystemName}
          />
        ),
      },
      {
        id: "option-details",
        Component: (
          <div className="flex flex-col w-full p-2 gap-2">
            {selectedOption === IMPORT_OPTION_ID.BASE_SYSTEM ? (
              <ImportBaseSystemStep
                baseSystemName={baseSystemName}
                value={importOption}
                onChange={setImportOption}
              />
            ) : (
              <ImportTemplateStep
                collaborators={collaborators}
                sort={sort}
                onDisableNextButton={setIsNextButtonDisabled}
                onDataParsed={setParsedRows}
              />
            )}
          </div>
        ),
      },
    ],
    [selectedOption, baseSystemName, collaborators, sort, importOption],
  );

  const { activeStep, isFirstStep, isLastStep, goToNext, goBack, reset } =
    useMultiStepDialog({ totalSteps: steps.length });

  const handleOnHide = () => {
    reset();
    onHide();
  };

  const handleImport = () => {
    if (importOption === "no-balances" && allCollaboratorsHaveBalances) {
      Toast("info", BALANCES_STRINGS.IMPORT_ALL_NO_DATA_ALERT);
      return;
    }

    setProgressModal({ status: "loading" });

    doImport(
      { mode: importOption },
      {
        onSuccess: () => {
          setProgressModal({ status: "success" });
        },
        onError: (error) => {
          setProgressModal({
            status: "error",
            message: error.message,
          });
        },
      },
    );
  };

  const handleProgressModalClose = () => {
    const wasSuccess = progressModal?.status === "success";
    setProgressModal(null);
    if (wasSuccess) {
      onImportSuccess();
      handleOnHide();
    }
  };

  const handleContinue = async () => {
    if (!isLastStep) {
      goToNext();
      return;
    }

    if (selectedOption === "import-base-system") {
      handleImport();
      return;
    }

    if (selectedOption === "import-template") {
      setProgressModal({ status: "loading" });
      try {
        const result = await loadInitialDataAsync(parsedRows);

        if (result.errors.length > 0) {
          const collaboratorNameById = new Map(
            collaborators.map((collaborator) => [
              collaborator.id,
              collaborator.fullName ?? collaborator.code ?? collaborator.id,
            ]),
          );

          setProgressModal({
            status: "error",
            errors: result.errors.map((rowError) => ({
              rowNumber: rowError.rowNumber,
              collaborator:
                collaboratorNameById.get(rowError.collaboratorId) ??
                rowError.collaboratorId,
              error: rowError.error,
            })),
          });
        } else {
          setProgressModal({ status: "success" });
        }
      } catch (error) {
        setProgressModal({
          status: "error",
          message: (error as Error).message,
        });
      }
    }
  };

  const secondStepHeader =
    selectedOption === IMPORT_OPTION_ID.BASE_SYSTEM
      ? BALANCES_STRINGS.DIALOG_HEADER_BASE_SYSTEM
      : BALANCES_STRINGS.DIALOG_HEADER_TEMPLATE;

  const header = isFirstStep
    ? BALANCES_STRINGS.INITIAL_LOADING_LABEL
    : secondStepHeader;

  return (
    <>
      <Dialog
        header={header}
        visible
        style={{ width: "90vw", maxWidth: "50rem", height: "28.5rem" }}
        onHide={handleOnHide}
        footer={
          <div className="flex justify-end gap-1">
            <ColaboraButton
              className="!w-[8rem]"
              label={
                isFirstStep
                  ? BALANCES_STRINGS.BUTTON_CANCEL
                  : BALANCES_STRINGS.BUTTON_BACK
              }
              variant="secondary"
              onClick={isFirstStep ? handleOnHide : goBack}
            />
            <ColaboraButton
              className="!w-[8rem]"
              label={BALANCES_STRINGS.BUTTON_CONTINUE}
              onClick={handleContinue}
              isLoading={isPending}
              disabled={isLastStep && isNextButtonDisabled}
            />
          </div>
        }
      >
        <div className="overflow-hidden">
          <div
            className="grid transition-transform duration-300"
            style={{
              gridTemplateColumns: `repeat(${steps.length}, 100%)`,
              transform: `translateX(${activeStep * -100}%)`,
            }}
          >
            {steps.map(({ id, Component }) => (
              <div key={id}>{Component}</div>
            ))}
          </div>
        </div>
      </Dialog>
      {progressModal && (
        <ImportProgressModal
          selectedOption={selectedOption}
          status={progressModal.status}
          texts={
            selectedOption === "import-template"
              ? BALANCES_TEMPLATE_IMPORT_TEXTS
              : BALANCES_IMPORT_TEXTS
          }
          errorMessage={
            progressModal.status === "error" && !progressModal.errors
              ? progressModal.message
              : undefined
          }
          errorList={
            progressModal.status === "error" ? progressModal.errors : undefined
          }
          onClose={handleProgressModalClose}
        />
      )}
    </>
  );
};

export default InitialLoadDialog;
